import CloseIcon from "@mui/icons-material/Close";
import TranslateIcon from "@mui/icons-material/Translate";
import {
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Grid,
    IconButton,
    MenuItem,
    Stack,
    Typography,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import axios from "axios";
import { useTranslation } from "react-i18next";
import React, {
    forwardRef,
    useImperativeHandle,
    useRef,
    useState,
} from "react";
import { useSelector } from "react-redux";
import { BANNER_POSITION } from "../../constants/Variables";
import globalStyle from "../../globalStyle";
import { baseUrl } from "../../services";
import { capitalizeText, handleOpenSnackBar } from "../../utils";
import { translateText } from "../../utils/translationService";
import AppLoader from "../AppLoader";
import GlobalButton from "../CustomButton";
import CustomDropDown from "../CutomDropDown";
import Label from "../Label";
import TextInput from "../TextInputs";
import { addEditBannerValidation } from "../../utils/Validation";
import { createBanner, updateBanner } from "../../services/banner";
import UploadBox from "../UploadBox";
import { APP_IMAGES } from "../../assets";
import DialogHeader from "./Header";
import TvIcon from '@mui/icons-material/Tv';
import DialogBody from "./Body";
import DialogActionButtons from "./Footer";

const BootstrapDialog = styled(Dialog)(({ theme }) => ({
    "& .MuiDialogContent-root": {
        padding: theme.spacing(4, 2),
    },
    "& .MuiDialogActions-root": {
        padding: theme.spacing(2),
    },
    "& .MuiPaper-root": {
        // width: "600px",
        [theme.breakpoints.down("sm")]: {
            margin: 12,
        },
    },
}));

const IMAGE_DIMENSIONS = {
    desktop: {
        minWidth: 1870,
        minHeight: 650,
        ratio: 1870 / 650,
    },
    mobile: {
        minWidth: 325,
        minHeight: 501,
        ratio: 325 / 501,
    },
};

let paramsData = {};

const initialInputValues = {
    image_desktop_en: null,
    image_desktop_ro: null,
    image_mobile_en: null,
    image_mobile_ro: null,
    title_en: "",
    title_ro: "",
    order: 0,
    link: "",
    position: "",
};

const AddEditBannerDialog = forwardRef(({ }, ref) => {
    const openImageDesktopFileEn = useRef(null);
    const openImageDesktopFileRo = useRef(null);
    const openImageMobileFileEn = useRef(null);
    const openImageMobileFileRo = useRef(null);
    const { t } = useTranslation(["catalogue", "common"]);
    const [open, setOpen] = useState(false);
    const [isLoading, setIsLoading] = useState({
        save: false,
        translateToEnglish: false,
        image_desktop_en: false,
        image_desktop_ro: false,
        image_mobile_en: false,
        image_mobile_ro: false,
    });
    const [inputError, setInputError] = useState({});
    const [inputValues, setInputValues] = useState(initialInputValues);

    // Redux State
    const searchKeys = useSelector((state) => state.searchKeysReducer.searchKeys);
    const currentLanguage = useSelector(
        (state) => state.languageReducer.currentLanguage,
    );

    useImperativeHandle(ref, () => ({
        openDialog(params) {
            setOpen(true);
            paramsData = params;
            if (params.type === "edit") {
                const data = params.data;
                setInputValues({
                    image_desktop_en: data?.image?.desktop?.en || null,
                    image_desktop_ro: data?.image?.desktop?.ro || null,
                    // image_mobile_en: data?.image?.mobile?.en || null,
                    // image_mobile_ro: data?.image?.mobile?.ro || null,
                    title_en: capitalizeText(data?.title?.en) || "",
                    title_ro: capitalizeText(data?.title?.ro) || "",
                    order: data?.order || 0,
                    position: data?.position || "",
                    link: data?.link || "",
                });
            }
        },

        closeDialog() {
            handleClose();
        },
    }));

    const handleClose = () => {
        setOpen(false);
        setInputValues(initialInputValues);
        setInputError({});
    };

    const handleAddEdit = () => {
        const valid = addEditBannerValidation(inputValues, setInputError);

        if (valid) {
            return handleOpenSnackBar("error", t("resolveTheIssues", { ns: "common" }));
        }

        let payload = {
            image: {
                desktop: {
                    en: inputValues.image_desktop_en,
                    ro: inputValues.image_desktop_ro,
                },
                mobile: {
                    en: null,
                    ro: null,
                },
            },
            title: {
                en: inputValues.title_en,
                ro: inputValues.title_ro,
            },
            order: inputValues.order,
            position: inputValues.position,
            link: inputValues.link,
        };

        if (paramsData?.type === "add") {
            handleApiCall(createBanner, payload, "");
        } else {
            handleApiCall(updateBanner, payload, paramsData?.data?._id);
        }
    };

    const handleApiCall = async (apiFunction, payload, id) => {
        try {
            setIsLoading((prev) => ({ ...prev, save: true }));
            const response = await apiFunction(id, payload);
            if (response?.data?.status === "success") {
                handleOpenSnackBar("success", response?.data?.message);
                handleClose();
                paramsData?.getData(searchKeys.bannerTitle);
            } else {
                handleOpenSnackBar("error", response?.data?.message);
            }
        } catch (error) {
            handleOpenSnackBar("error", t("error.somethingWentWrong", { ns: "common" }));
        } finally {
            setIsLoading((prev) => ({ ...prev, save: false }));
        }
    };

    const handleTranslateToEnglish = async (key1, key2) => {
        setIsLoading((prev) => ({ ...prev, translateToEnglish: true }));
        try {
            const [englishText] = await Promise.all([
                translateText(inputValues[key1]),
            ]);
            setInputValues((prev) => ({
                ...prev,
                [key2]: englishText,
            }));
            setInputError((prev) => ({
                ...prev,
                [key2]: "",
            }));
        } catch (error) {
            handleOpenSnackBar("error", t("error.errorWhileTranslating", { ns: "common" }));
        } finally {
            setIsLoading((prev) => ({ ...prev, translateToEnglish: false }));
        }
    };

    const handleChageInputs = (e) => {
        const { name, value, valueAsNumber, type } = e.target;
        const formatedValue = type === "number" ? valueAsNumber : value;

        setInputValues((pre) => ({ ...pre, [name]: formatedValue }));
        setInputError((pre) => ({ ...pre, [name]: "" }));
    };

    const handleUploadImage = async (event, type, screenType) => {
        const file = event.target.files[0];
        if (!file) return;

        const img = new Image();
        img.src = URL.createObjectURL(file);

        img.onload = async () => {
            const { width, height } = img;

            const isMobile = screenType === "mobile";
            const requiredSize = isMobile
                ? IMAGE_DIMENSIONS.mobile
                : IMAGE_DIMENSIONS.desktop;

            const ratio = width / height;
            const allowedDiff = 0.03; // tolerance

            // ✅ VALIDATION (BEST PRACTICE)
            if (
                width < requiredSize.minWidth ||
                height < requiredSize.minHeight ||
                Math.abs(ratio - requiredSize.ratio) > allowedDiff
            ) {
                handleOpenSnackBar(
                    "error",
                    t("validation.invalidImageSize", {
                        screenType,
                        minWidth: requiredSize.minWidth,
                        minHeight: requiredSize.minHeight,
                    }),
                );
                event.target.value = null;
                return;
            }

            try {
                setIsLoading((prev) => ({ ...prev, [type]: true }));

                const formData = new FormData();
                formData.append("image", file);

                const response = await axios({
                    method: "post",
                    url: `${baseUrl}image/upload`,
                    data: formData,
                    headers: { "Content-Type": "multipart/form-data" },
                });

                setInputError((prev) => ({ ...prev, [type]: false }));
                setInputValues((prev) => ({
                    ...prev,
                    [type]: response?.data?.url,
                }));

                handleOpenSnackBar("success", t("success.mediaUploadSuccess", { ns: "common" }));
            } catch (error) {
                handleOpenSnackBar("error", t("error.mediaUploadFail", { ns: "common" }));
            } finally {
                // reset file inputs
                if (openImageDesktopFileEn.current)
                    openImageDesktopFileEn.current.value = null;
                if (openImageDesktopFileRo.current)
                    openImageDesktopFileRo.current.value = null;
                if (openImageMobileFileEn.current)
                    openImageMobileFileEn.current.value = null;
                if (openImageMobileFileRo.current)
                    openImageMobileFileRo.current.value = null;

                setIsLoading((prev) => ({ ...prev, [type]: false }));
            }
        };
    };

    return (
        <>
            <AppLoader isVisible={isLoading.save} />
            <BootstrapDialog
                onClose={handleClose}
                aria-labelledby="customized-dialog-title"
                open={open}
                fullWidth
                maxWidth="md"
            >


                <DialogHeader
                    title={paramsData.type === "add"
                        ? t("addBanner")
                        : t("editBanner")}
                    secondaryHeading={paramsData.type === "add"
                        ? t("addBannerInstruction")
                        : t("editBannerInstruction")}
                    icon={<TvIcon />}
                    onClose={handleClose}
                />

                <DialogBody>
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <UploadBox
                                inputRef={openImageDesktopFileEn}
                                label={t("imageDesktopEn")}
                                note={t("imageDesktopEnSizeNote")}
                                value={inputValues.image_desktop_en}
                                error={inputError.image_desktop_en}
                                loading={isLoading.image_desktop_en}
                                placeholderImage={APP_IMAGES.uploadImage}
                                accept="image/png, image/jpeg, image/jpg, image/webp"
                                height={{ xs: 200, md: 130 }}
                                previewSizeProps={{
                                    width: { xs: "100%", md: "100%" },
                                    height: { xs: "auto", md: "auto" },
                                    p: 0,
                                    overflow: "hidden",
                                }}
                                imageProps={{
                                    width: "100%",
                                    height: "100%",
                                    sx: { objectFit: "fill" },
                                }}
                                onClick={() => openImageDesktopFileEn.current.click()}
                                onChange={(event) =>
                                    handleUploadImage(event, "image_desktop_en", "desktop")
                                }
                                onRemove={() =>
                                    setInputValues({ ...inputValues, image_desktop_en: null })
                                }
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <UploadBox
                                inputRef={openImageDesktopFileRo}
                                label={t("imageDesktopRo")}
                                note={t("imageDesktopRoSizeNote")}
                                value={inputValues.image_desktop_ro}
                                error={inputError.image_desktop_ro}
                                loading={isLoading.image_desktop_ro}
                                placeholderImage={APP_IMAGES.uploadImage}
                                accept="image/png, image/jpeg, image/jpg, image/webp"
                                height={{ xs: 200, md: 130 }}
                                previewSizeProps={{
                                    width: { xs: "100%", md: "100%" },
                                    height: { xs: "auto", md: "auto" },
                                    p: 0,
                                    overflow: "hidden",
                                }}
                                imageProps={{
                                    width: "100%",
                                    height: "100%",
                                    sx: { objectFit: "fill" },
                                }}
                                onClick={() => openImageDesktopFileRo.current.click()}
                                onChange={(event) =>
                                    handleUploadImage(event, "image_desktop_ro", "desktop")
                                }
                                onRemove={() =>
                                    setInputValues({ ...inputValues, image_desktop_ro: null })
                                }
                            />
                        </Grid>
                        {/* Mobile banner upload temporarily disabled */}
                        {/* <Grid item xs={12}>
                            <UploadBox
                                inputRef={openImageMobileFileEn}
                                label={t("imageMobileEn")}
                                note={t("imageMobileEnSizeNote")}
                                value={inputValues.image_mobile_en}
                                error={inputError.image_mobile_en}
                                loading={isLoading.image_mobile_en}
                                placeholderImage={APP_IMAGES.uploadImage}
                                accept="image/png, image/jpeg, image/jpg, image/webp"
                                height={{ xs: 200, md: 130 }}
                                previewSizeProps={{
                                    width: { xs: "100%", md: "357px" },
                                    height: { xs: "auto", md: "auto" },
                                    p: 0,
                                    overflow: "hidden",
                                }}
                                imageProps={{
                                    width: "100%",
                                    height: "100%",
                                    sx: { objectFit: "fill" },
                                }}
                                onClick={() => openImageMobileFileEn.current.click()}
                                onChange={(event) =>
                                    handleUploadImage(event, "image_mobile_en", "mobile")
                                }
                                onRemove={() =>
                                    setInputValues({ ...inputValues, image_mobile_en: null })
                                }
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <UploadBox
                                inputRef={openImageMobileFileRo}
                                label={t("imageMobileRo")}
                                note={t("imageMobileRoSizeNote")}
                                value={inputValues.image_mobile_ro}
                                error={inputError.image_mobile_ro}
                                loading={isLoading.image_mobile_ro}
                                placeholderImage={APP_IMAGES.uploadImage}
                                accept="image/png, image/jpeg, image/jpg, image/webp"
                                height={{ xs: 200, md: 130 }}
                                previewSizeProps={{
                                    width: { xs: "100%", md: "357px" },
                                    height: { xs: "auto", md: "auto" },
                                    p: 0,
                                    overflow: "hidden",
                                }}
                                imageProps={{
                                    width: "100%",
                                    height: "100%",
                                    sx: { objectFit: "fill" },
                                }}
                                onClick={() => openImageMobileFileRo.current.click()}
                                onChange={(event) =>
                                    handleUploadImage(event, "image_mobile_ro", "mobile")
                                }
                                onRemove={() =>
                                    setInputValues({ ...inputValues, image_mobile_ro: null })
                                }
                            />
                        </Grid> */}

                        <Grid item xs={12} md={6}>
                            <Label
                                required
                                labelText={t("order")}
                                htmlFor={"banner-order"}
                            />
                            <TextInput
                                id={"banner-order"}
                                styleProps={globalStyle.textField}
                                placeholder={t("enterOrder")}
                                required
                                name={"order"}
                                type={"number"}
                                value={inputValues.order}
                                onChange={handleChageInputs}
                                inputError={inputError.order}
                                helperText={t(inputError.order)}
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <Label
                                optional
                                labelText={t("redirectUrl")}
                                htmlFor={"banner-redirect-url"}
                            />
                            <TextInput
                                id={"banner-redirect-url"}
                                styleProps={globalStyle.textField}
                                placeholder={t("enterRedirectUrl")}
                                required
                                name={"link"}
                                type={"text"}
                                value={inputValues.link}
                                onChange={handleChageInputs}
                                inputError={inputError.link}
                                helperText={t(inputError.link)}
                            />
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <Label
                                required
                                labelText={t("position")}
                                htmlFor={"banner-position"}
                            />
                            <CustomDropDown
                                id={"banner-position"}
                                name={"position"}
                                value={inputValues.position}
                                onChange={handleChageInputs}
                                inputError={Boolean(inputError.position)}
                                errorText={t(inputError.position)}
                            >
                                <MenuItem value="" disabled>
                                    {t("selectPosition")}
                                </MenuItem>
                                {BANNER_POSITION.map((item) => (
                                    <MenuItem key={item.value} value={item.value}>
                                        {item.label[currentLanguage]}
                                    </MenuItem>
                                ))}
                            </CustomDropDown>
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <Label
                                required
                                labelText={t("titleInRomanian")}
                                htmlFor={`banner-title-ro`}
                            />
                            <TextInput
                                id={`banner-title-ro`}
                                required
                                placeholder={t("enterTitleInRomanian")}
                                name={`title_ro`}
                                textLength={60}
                                value={inputValues.title_ro}
                                onChange={handleChageInputs}
                                inputError={Boolean(inputError.title_ro)}
                                helperText={t(inputError.title_ro)}
                                InputEndIcon={
                                    <Typography variant="body2" color="text.grey">
                                        {inputValues.title_ro?.length}/60
                                    </Typography>
                                }
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <Stack
                                mb={"5px"}
                                direction={"row"}
                                alignItems={"center"}
                                justifyContent={"space-between"}
                            >
                                <Label
                                    required
                                    labelText={t("titleInEnglish")}
                                    htmlFor={`banner-title-en`}
                                    labelProps={{ marginBottom: 0 }}
                                />
                                <Chip
                                    label={t("translateToEnglish", { ns: "common" })}
                                    size="small"
                                    onClick={() =>
                                        handleTranslateToEnglish("title_ro", "title_en")
                                    }
                                    icon={
                                        isLoading.translateToEnglish ? (
                                            <CircularProgress size={16} color="inherit" />
                                        ) : (
                                            <TranslateIcon />
                                        )
                                    }
                                    disabled={isLoading.translateToEnglish}
                                />
                            </Stack>
                            <TextInput
                                id={`banner-title-en`}
                                required
                                placeholder={t("enterTitleInEnglish")}
                                name={`title_en`}
                                textLength={60}
                                value={inputValues.title_en}
                                onChange={handleChageInputs}
                                inputError={Boolean(inputError.title_en)}
                                helperText={t(inputError.title_en)}
                                InputEndIcon={
                                    <Typography variant="body2" color="text.grey">
                                        {inputValues.name_en?.length}/60
                                    </Typography>
                                }
                            />
                        </Grid>
                    </Grid>
                </DialogBody>
                <DialogActionButtons
                    onConfirm={handleAddEdit}
                    onCancel={handleClose}
                    confirmText={"save"}
                    cancelText={"cancel"}
                    isConfirmBtnDisable={isLoading.save}
                    showCancelButton={true}
                />
            </BootstrapDialog>
        </>
    );
});

export default AddEditBannerDialog;
